import React, { Suspense, lazy } from 'react';
import type { CustomField } from '@puckeditor/core';

import type { Slide } from '../../components/slideshow-contract';

export type SlideshowManagerFieldProps = {
  field: { label?: string };
  name: string;
  value?: Slide[] | null;
  readOnly?: boolean;
};

// The manager pulls in Puck's store, dnd-kit, the Asset Library data layer, and destination
// pickers. Keeping it behind this field-level boundary prevents that editor-only weight from
// entering the published storefront bundle through the shared component config.
const SlideshowManagerWidget = lazy(() => import('./slideshow-manager-widget'));

function SlideshowManagerFieldRender(props: SlideshowManagerFieldProps): JSX.Element {
  return (
    <Suspense fallback={null}>
      <SlideshowManagerWidget {...props} />
    </Suspense>
  );
}

export const slideshowManagerField: CustomField<Slide[]> = {
  type: 'custom',
  label: 'Slides',
  render: SlideshowManagerFieldRender as CustomField<Slide[]>['render'],
  ai: {
    schema: {
      type: 'array',
      maxItems: 10,
      items: {
        anyOf: [
          {
            type: 'object',
            required: ['src', 'alt', 'linkHref'],
            properties: {
              kind: { type: 'string', enum: ['image'] },
              src: { type: 'string' },
              mobileSrc: { type: 'string' },
              alt: {
                type: 'object',
                required: ['alt', 'decorative'],
                properties: { alt: { type: 'string' }, decorative: { type: 'boolean' } },
              },
              linkHref: { type: 'string' },
            },
          },
          {
            type: 'object',
            required: ['kind', 'specialId', 'src', 'alt'],
            properties: {
              kind: { type: 'string', enum: ['special'] },
              specialId: { type: 'string' },
              src: { type: 'string' },
              mobileSrc: { type: 'string' },
              alt: {
                type: 'object',
                required: ['alt', 'decorative'],
                properties: { alt: { type: 'string' }, decorative: { type: 'boolean' } },
              },
            },
          },
        ],
      },
    },
    stream: false,
    instructions:
      'An ordered list of at most 10 authored image or connected Special slides. Existing image rows may omit kind; a typed image uses kind "image" and may have an optional destination. Special rows use kind "special" plus specialId and authored imagery; never invent or store Special type, destination URL, title, or a copied Special object. An empty mobile image falls back to desktop.',
  },
};
